import React, { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Bell, BellOff, BellRing, Rss, Loader2, ExternalLink } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import PullToRefresh from '../../components/PullToRefresh'
import { performRefresh } from '../../lib/refresh'
import { useNewsArticles, useNewsSources } from '../../hooks/useNews'
import { useNewsSubscriptions } from '../../hooks/useNewsSubscriptions'
import { useAuth } from '../../contexts/AuthContext'
import { cn } from '../../lib/utils'

// Per-source feed at /news/source/:slug. Shows only articles from
// one source plus subscription controls (Off / Daily digest / Instant).

const MODES = [
  { value: 'off',     icon: BellOff,  labelKey: 'newsSource.modeOff' },
  { value: 'daily',   icon: Bell,     labelKey: 'newsSource.modeDaily' },
  { value: 'instant', icon: BellRing, labelKey: 'newsSource.modeInstant' },
]

export function NewsSourcePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slug } = useParams()
  const { user } = useAuth()

  const sourcesHook = useNewsSources()
  const subs = useNewsSubscriptions()
  const source = useMemo(
    () => (sourcesHook.sources || []).find((s) => s.slug === slug),
    [sourcesHook.sources, slug],
  )

  const articlesHook = useNewsArticles({
    sourceId: source?.id,
    language: i18n.language === 'ar' ? 'ar' : 'en',
    pageSize: 30,
  })

  const handleRefresh = useCallback(
    () => performRefresh([sourcesHook.refetch, articlesHook.refetch, subs.refetch]),
    [sourcesHook.refetch, articlesHook.refetch, subs.refetch],
  )

  if (sourcesHook.loading && !source) {
    return <div className="p-4"><Skeleton className="h-24 w-full" /></div>
  }
  if (!source) {
    return (
      <div className="p-4 space-y-3">
        <button type="button" onClick={() => navigate('/news')} className="text-sm text-app-primary-500">
          ← {t('news.title')}
        </button>
        <p className="text-sm text-muted-foreground" dir="auto">{t('newsSource.notFound')}</p>
      </div>
    )
  }

  const existing = subs.bySource.get(source.id)
  const currentMode = existing?.notify_mode || 'off'

  const handleMode = async (mode) => {
    if (mode === 'off') {
      if (existing) await subs.unsubscribe({ source_id: source.id })
    } else {
      await subs.subscribe({ source_id: source.id, notify_mode: mode })
    }
  }

  const articles = articlesHook.articles || []

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-4 content-fade pb-8">
        {/* Header */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/news')}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2" dir="auto">
              <Rss className="w-4 h-4 text-app-primary-500" />
              {source.name}
            </h1>
            <p className="text-[11px] text-muted-foreground" dir="auto">
              {source.kind} · {source.language || '—'}
              {source.category && ` · ${source.category}`}
              {' · '}
              {articles.length} {t('newsSource.articles')}
            </p>
          </div>
          {source.homepage_url && (
            <a
              href={source.homepage_url}
              target="_blank"
              rel="noreferrer"
              aria-label="Open source homepage"
              className="p-2 rounded-md text-muted-foreground hover:bg-accent"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Subscription controls */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2">
              <BellRing className="w-4 h-4 text-app-primary-500" />
              <p className="text-sm font-semibold text-foreground" dir="auto">
                {t('newsSource.notifyMe')}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {MODES.map((m) => {
                const Icon = m.icon
                const active = currentMode === m.value
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => handleMode(m.value)}
                    className={cn(
                      'flex flex-col items-center gap-1 py-2.5 rounded-lg border transition-colors',
                      active
                        ? 'border-app-primary-500 bg-app-primary-500/10 text-app-primary-500'
                        : 'border-input text-muted-foreground hover:bg-accent',
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[11px] font-medium" dir="auto">{t(m.labelKey)}</span>
                  </button>
                )
              })}
            </div>
            <p className="text-[10px] text-muted-foreground pt-1" dir="auto">
              {currentMode === 'instant'
                ? t('newsSource.modeInstantHint')
                : currentMode === 'daily'
                  ? t('newsSource.modeDailyHint')
                  : t('newsSource.modeOffHint')}
            </p>
          </CardContent>
        </Card>

        {/* Articles list */}
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1" dir="auto">
            {t('newsSource.latestArticles')}
          </h2>
          {articlesHook.loading ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : articles.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
                {t('newsSource.empty')}
              </CardContent>
            </Card>
          ) : (
            articles.map((a) => (
              <Link key={a.id} to={`/news/${a.id}`} className="block">
                <Card className="hover:border-app-primary-500/40 transition-colors">
                  <CardContent className="p-3 space-y-1.5">
                    <p className="text-sm font-medium text-foreground line-clamp-2" dir="auto">
                      {a.title}
                    </p>
                    {a.summary && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2" dir="auto">
                        {a.summary}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground" dir="ltr">
                      {a.published_at ? a.published_at.slice(0, 16).replace('T', ' ') : ''}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </section>
      </div>
    </PullToRefresh>
  )
}
